import {
    Module,
    VuexModule,
    getModule,
    Action,
    Mutation,
} from 'vuex-module-decorators';
import store from '~common-store';
import { MappedOwner, OwnersState, OwnerType } from '~/interfaces/owners.interface';
import apiService from '~/services/api.service';
import { mapUsersDetails } from '~/helpers';
import { HomesModule } from '~/store/modules/homes';
import { DevicesModule } from '~/store/modules/devices';

@Module({ dynamic: true, store, name: 'owners', namespaced: true })
class Owners extends VuexModule implements OwnersState {
    homeId = '';
    owners = [] as MappedOwner[];
    isLoading = false;

    @Action
    async getOwners({ homeId, deviceId }: { homeId?: string | null, deviceId?: string }) {
        let shouldSetOwners = false;
        try {
            this.context.commit('SET_IS_LOADING', true);
            let owners: OwnerType[] = [];
            if (homeId) {
                owners = (await apiService.gethomeowners({ home_id: homeId })).data.body as OwnerType[];
            } else if (deviceId) {
                owners = (await apiService.getdeviceowners({ device_id: deviceId })).data.body as OwnerType[];
            }
            // Do not update owners if we switched home or device before the backend responded. Update
            // only when the call for our current home or device has finished
            shouldSetOwners = Boolean((homeId === HomesModule.currentHomeId)
                || (deviceId && (deviceId === DevicesModule.currentGatewayId)));
            if (shouldSetOwners) {
                this.setOwners({ homeId, owners });
            }
        } catch (error) {
            return;
        } finally {
            if (shouldSetOwners) {
                this.context.commit('SET_IS_LOADING', false);
            }
        }
    }

    @Action
    setOwners({ homeId, owners }: { homeId?: string | null, owners: OwnerType[] }) {
        let mappedOwners = mapUsersDetails(owners);
        // instantiate array to keep track of owners that will need to be removed after being moved under their linked user
        const filteredOwners: string[] = [];
        mappedOwners.forEach((owner) => {
            owner.linkedUsers.forEach((linkedUser) => {
                const mappedOwner = mappedOwners.find((owner) => owner.id === linkedUser);
                if (mappedOwner) {
                    owner.mappedLinkedUsers = owner.mappedLinkedUsers || [];
                    owner.mappedLinkedUsers.push(mappedOwner);
                    // add linked user id to list of owners to remove
                    filteredOwners.push(linkedUser);
                }
            });
        });
        mappedOwners = mappedOwners.filter((mappedOwner) => !filteredOwners.includes(mappedOwner.id));
        this.context.commit('SET_OWNERS', { homeId, owners: mappedOwners });
    }

    @Mutation
    SET_OWNERS({ homeId, owners }: { homeId: string, owners: MappedOwner[] }) {
        this.homeId = homeId;
        this.owners = owners;
    }

    @Mutation
    SET_IS_LOADING(isLoading: boolean) {
        this.isLoading = isLoading;
    }
}

export const OwnerModule = getModule(Owners);
